import api from './api';
import { AxiosProgressEvent } from 'axios';

export interface YouTubeNotification {
  id?: number;
  stt: number;
  quan_doan?: string;
  mon_phai?: string;
  viral_key?: string;
  rpm?: string;
  title: string;
  views: number;
  ngay_dang?: string;
  youtube_link: string;
  is_active: boolean;
  display_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface YouTubeNotificationDisplay {
  title: string;
  youtube_link: string;
  quan_doan?: string;
  mon_phai?: string;
  viral_key?: string;
  ngay_dang?: string;
}

export interface PaginatedResponse<T> {
  notifications: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export const youtubeNotificationService = {
  // Public - Get active notifications
  getActiveNotifications: async (): Promise<YouTubeNotificationDisplay[]> => {
    const response = await api.get<{ notifications: YouTubeNotificationDisplay[] }>(
      '/youtube-notifications/public/active'
    );
    return response.data.notifications;
  },

  // Admin - List all notifications
  list: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    is_active?: string;
  }): Promise<PaginatedResponse<YouTubeNotification>> => {
    const response = await api.get<PaginatedResponse<YouTubeNotification>>(
      '/youtube-notifications/admin/list',
      { params }
    );
    return response.data;
  },

  // Admin - Get single notification
  get: async (id: number): Promise<YouTubeNotification> => {
    const response = await api.get<{ notification: YouTubeNotification }>(
      `/youtube-notifications/admin/${id}`
    );
    return response.data.notification;
  },

  // Admin - Create notification
  create: async (data: Partial<YouTubeNotification>): Promise<YouTubeNotification> => {
    const response = await api.post<{
      message: string;
      notification: YouTubeNotification;
    }>('/youtube-notifications/admin/create', data);
    return response.data.notification;
  },

  // Admin - Update notification
  update: async (
    id: number,
    data: Partial<YouTubeNotification>
  ): Promise<YouTubeNotification> => {
    const response = await api.put<{
      message: string;
      notification: YouTubeNotification;
    }>(`/youtube-notifications/admin/${id}`, data);
    return response.data.notification;
  },

  // Admin - Delete notification
  delete: async (id: number): Promise<void> => {
    await api.delete(`/youtube-notifications/admin/${id}`);
  },

  // Admin - Bulk delete notifications
  bulkDelete: async (notificationIds: number[]): Promise<{ message: string; deleted_count: number }> => {
    const response = await api.post<{
      message: string;
      deleted_count: number;
    }>('/youtube-notifications/admin/bulk-delete', { notification_ids: notificationIds });
    return response.data;
  },

  // Admin - Import from Excel
  importExcel: async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{ message: string; created: number; errors?: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<{
      message: string;
      created: number;
      errors?: string[];
    }>('/youtube-notifications/admin/import-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  },

  // Admin - Export to Excel
  exportExcel: async (): Promise<Blob> => {
    const response = await api.get('/youtube-notifications/admin/export-excel', {
      responseType: 'blob',
    });
    return response.data;
  },
};

